import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { t } from "i18next";
import debounce from "lodash/debounce";
import isEmpty from "lodash/isEmpty";
import moment from "moment";
import { FcCalendar } from "react-icons/fc";
import { useDispatch, useSelector } from "react-redux";
import { useAppMode } from "src/contexts/app-mode";
import LocationPinIcon from "src/images/icons/location-pin.svg";
import TimeIcon from "src/images/icons/time.svg";

import { BOOKING_CONSTANTS, PAYMENT_OPTIONS } from "@constants/Bookings";
import { DRAWER_HEIGHT, DrawerTimeSlotProps } from "@constants/Drawer";
import { CONSTANTS } from "@constants/index";
import { BOOKING_DRAWER_STEPS } from "@enums/component.enum";
import { DateFormats } from "@enums/shared.enum";
import { useBooking } from "@hooks/useBooking/useBooking";
import { useBookingSubmit } from "@hooks/useBookingSubmit/useBookingSubmit";
import { usePricing } from "@hooks/usePricing/usePricing";
import { AmenityAPIResponse } from "@models/services/amenity";
import {
  BookingFetchResponseType,
  ResourceType,
  SelectedAmenity,
} from "@models/services/booking";
import { applyLocalization } from "@services/common.service";
import bookingSlice from "@slices/booking.slice";
import bookingCalendarSlice from "@slices/bookingCalendar.slice";
import drawerSlice from "@slices/drawer.slice";
import { RootState } from "@store/index";
import {
  checkIsAmenityDisabled,
  isAmenityApplicable,
} from "@utils/amenity.utils";
import { buildTempEvent } from "@utils/booking.utils";

import BookingDrawerStepOne from "../BookingDrawer/BookingDrawerStepOne/BookingDrawerStepOne";
import BookingDrawerStepTwo from "../BookingDrawer/BookingDrawerStepTwo/BookingDrawerStepTwo";
import Terms from "../BookingForm/Terms/Terms";
import TokenisationConfirmModal from "../TokenisationConfirmModal/TokenisationConfirmModal";

const DrawerTimeSlot = ({
  bookingSlots,
  isBookingDisable,
  locationContext,
  priceLoader,
}: DrawerTimeSlotProps) => {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [currentStep, setCurrentStep] = useState<BOOKING_DRAWER_STEPS>(
    BOOKING_DRAWER_STEPS.ONE
  );
  const [guestError, setGuestError] = useState("");
  const [selectedAmenitie, setSelectedAmenities] = useState<SelectedAmenity[]>(
    []
  );
  const [amenitiesList, setAmenitiesList] = useState<AmenityAPIResponse[]>([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [showPaymentConfirmModal, setShowPaymentConfirmModal] = useState(false);

  const dispatch = useDispatch();
  const { isKioskMode } = useAppMode();

  const {
    amenityAddedUnavailableError,
    bookingEditData,
    gdprAccepted,
    guestList,
    isEditBooking,
    legalTermsAccepted,
    paymentLoader,
    resourcePrices,
    selectedDate,
    selectedSlot,
    selectedTime,
    selectedTokenizationId,
  } = useSelector((State: RootState) => State.booking);

  const openPercent = useSelector(
    (state: RootState) => state.drawer.openPercent
  );

  const drawerBookingData = useSelector(
    (State: RootState) => State.drawer.drawerBookingData
  );
  const showBookingDrawer = useSelector(
    (State: RootState) => State.drawer.showBookingDrawer
  );
  const kioskBookingJustSaved = useSelector(
    (state: RootState) => state.kiosk.kioskBookingJustSaved
  );
  const { resourceId } = drawerBookingData;
  const locationId = locationContext?.id;

  const { fetchAmenities, getSelectedResource } = useBooking();

  const drawerContainerRef = useRef<HTMLDivElement>(null);

  const { fetchResourcesPrices, getPriceByResourceSlot } = usePricing();

  const hasValidResource = resourceId !== undefined;
  const isFinanceConfigured = locationContext?.financeConfigured || false;

  const fetchPricesRef = useRef(fetchResourcesPrices);
  fetchPricesRef.current = fetchResourcesPrices;

  const debouncedFetchPrices = useMemo(
    () =>
      debounce(
        (
          slots: BookingFetchResponseType,
          isEdit: boolean,
          amenities: SelectedAmenity[],
          amenityList: AmenityAPIResponse[]
        ) => {
          fetchPricesRef.current(slots, isEdit, amenities, amenityList);
        },
        300
      ),
    []
  );

  useEffect(() => {
    return () => debouncedFetchPrices.cancel();
  }, [debouncedFetchPrices]);

  const { guestPriceAmount, priceForResource, totalPriceWithTax } =
    useMemo(() => {
      const {
        guestPrice = 0,
        price = 0,
        totalPriceWithTax = 0,
      } = getPriceByResourceSlot(
        resourcePrices,
        resourceId,
        selectedSlot?.slot?.slot
      ) || {};

      const numPrice = Number(price);
      const numGuestPrice = Number(guestPrice);

      return {
        guestPriceAmount: Number.isNaN(numGuestPrice) ? 0 : numGuestPrice,
        priceForResource: Number.isNaN(numPrice) ? 0 : numPrice,
        totalPriceWithTax,
      };
    }, [
      resourcePrices,
      resourceId,
      selectedSlot?.slot?.slot,
      getPriceByResourceSlot,
    ]);

  useEffect(() => {
    setGuestError("");
  }, [guestList, selectedSlot]);

  // Track all amenities encountered during this session to get names for unavailable selections
  const encounteredAmenitiesRef = useRef<Record<string, AmenityAPIResponse>>(
    {}
  );

  useEffect(() => {
    amenitiesList.forEach(a => {
      encounteredAmenitiesRef.current[a.id] = a;
    });
  }, [amenitiesList]);

  const availableAmenities = useMemo(() => {
    // Current available ones from API
    const list = amenitiesList.filter(amenity => {
      const isDisabled = checkIsAmenityDisabled(
        amenity,
        selectedSlot,
        selectedTime,
        selectedDate
      );
      return !isDisabled;
    });

    // Plus any selected ones that are NOT in the list (now unavailable for this duration)
    selectedAmenitie.forEach(sa => {
      if (sa.isBooked) return;
      const exists = list.some(a => a.id === sa.id);
      if (!exists) {
        const encountered = encounteredAmenitiesRef.current[sa.id];
        if (encountered) {
          list.push(encountered);
        }
      }
    });

    return list;
  }, [
    amenitiesList,
    selectedAmenitie,
    selectedSlot,
    selectedTime,
    selectedDate,
  ]);

  useEffect(() => {
    if (
      locationId &&
      selectedDate &&
      selectedTime &&
      selectedSlot?.slot?.slot
    ) {
      fetchAmenities({
        bookingDate: selectedDate,
        locationId,
        slotTime: selectedSlot?.slot?.slot,
        startTime: selectedTime,
      }).then((data: AmenityAPIResponse[] | null) => {
        if (data) {
          setAmenitiesList(data);
        }
      });
    }
  }, [selectedSlot?.slot?.slot]);

  useEffect(() => {
    if (isBookingDisable || !selectedTime || isEmpty(bookingSlots?.resources)) {
      return;
    }

    dispatch(bookingSlice.actions.setPriceLoader(true));
    debouncedFetchPrices(
      bookingSlots,
      CONSTANTS.IS_NOT_EDIT_BOOKING,
      selectedAmenitie,
      availableAmenities
    );
  }, [
    selectedTime,
    guestList,
    selectedAmenitie,
    isFinanceConfigured,
    bookingSlots?.resources?.length,
  ]);

  useEffect(() => {
    if (isKioskMode && !selectedTime && kioskBookingJustSaved) {
      setSelectedAmenities([]);
    }
    setAmenitiesList([]);
  }, [selectedTime, isKioskMode, kioskBookingJustSaved]);

  useEffect(() => {
    setCurrentStep(BOOKING_DRAWER_STEPS.ONE);
    if (isKioskMode && !showBookingDrawer && kioskBookingJustSaved) {
      setSelectedAmenities([]);
    }
  }, [showBookingDrawer, isKioskMode, kioskBookingJustSaved]);

  const isPriceFree = priceForResource === 0;

  const canProceedToNextStep = Boolean(
    selectedTime &&
      selectedSlot?.resourceId &&
      selectedSlot?.isBookable !== false
  );

  const currentResource: ResourceType = useMemo(
    () => getSelectedResource(bookingSlots, resourceId),
    [bookingSlots, resourceId]
  );

  const allowBringGuest = useMemo(
    () =>
      Boolean(
        currentResource?.policyInfo?.allowBringGuest &&
          currentResource?.slotInfo?.slots?.allowedGuestTimings.length > 0
      ),
    [currentResource]
  );

  const currency = resourcePrices?.currency || "";

  const { handleBookingSubmit } = useBookingSubmit({
    amenitiesList: availableAmenities,
    grandTotal,
    selectedAmenitie,
    selectedPaymentMethod,
    setGuestError,
    totalPriceWithTax,
  });

  const handleBack = () => {
    if (currentStep === BOOKING_DRAWER_STEPS.TWO) {
      setCurrentStep(BOOKING_DRAWER_STEPS.ONE);
      scrollToTop();
    } else if (currentStep === BOOKING_DRAWER_STEPS.ONE) {
      dispatch(
        drawerSlice.actions.setOpenPercent(DRAWER_HEIGHT.SECONDARY_POSITION)
      );
    }
  };

  const scrollToTop = useCallback(() => {
    drawerContainerRef.current?.scrollTo({ behavior: "smooth", top: 0 });
  }, []);

  const isSavedCardSelected =
    selectedPaymentMethod === PAYMENT_OPTIONS.INSTANT_PAYMENT &&
    !!selectedTokenizationId &&
    selectedTokenizationId !== "new";

  const isNoAmountChange =
    isEditBooking &&
    bookingEditData?.totalPrice !== undefined &&
    grandTotal <= bookingEditData.totalPrice;

  const currentResourcePrice = resourceId
    ? resourcePrices?.prices?.[resourceId]
    : undefined;

  const isTokenizationAllowed =
    currentResourcePrice &&
    typeof currentResourcePrice.allowTokenization === "boolean"
      ? currentResourcePrice.allowTokenization
      : true;

  const executeBookingSubmit = useCallback(() => {
    if (
      isTokenizationAllowed &&
      selectedPaymentMethod === PAYMENT_OPTIONS.INSTANT_PAYMENT &&
      (!selectedTokenizationId || selectedTokenizationId === "new") &&
      !isNoAmountChange
    ) {
      setShowPaymentConfirmModal(true);
      return;
    }
    handleBookingSubmit();
  }, [
    selectedPaymentMethod,
    selectedTokenizationId,
    isNoAmountChange,
    handleBookingSubmit,
  ]);

  const scrollToFirstError = useCallback(() => {
    setTimeout(() => {
      const errorElement = document.querySelector(".alert-danger");
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 1000);
  }, []);

  const onConfirmPayment = useCallback(() => {
    const isSuccess = handleBookingSubmit();
    if (!isSuccess) {
      setShowPaymentConfirmModal(false);
      scrollToFirstError();
      return;
    }
    setShowPaymentConfirmModal(false);
  }, [handleBookingSubmit, scrollToFirstError]);

  const handleNext = useCallback(() => {
    if (!canProceedToNextStep) {
      return;
    }

    if (currentStep === BOOKING_DRAWER_STEPS.ONE) {
      const shouldGoToStepTwo =
        !isPriceFree ||
        allowBringGuest ||
        availableAmenities.length > CONSTANTS.DEFAULT_VALUE;

      if (shouldGoToStepTwo) {
        setCurrentStep(BOOKING_DRAWER_STEPS.TWO);
        scrollToTop();
      } else {
        executeBookingSubmit();
      }
      return;
    }

    executeBookingSubmit();
  }, [
    canProceedToNextStep,
    currentStep,
    isPriceFree,
    allowBringGuest,
    scrollToTop,
    executeBookingSubmit,
  ]);

  const bookingUrls = {
    gdpr: locationContext?.gdprUrl,
    gdprTerms: locationContext?.gdprTerms,
    legal_terms: locationContext?.termsUrl,
    termsService: locationContext?.termsService,
  };

  const canSkipStepTwo =
    !allowBringGuest &&
    isPriceFree &&
    availableAmenities.length === CONSTANTS.DEFAULT_VALUE &&
    !isEmpty(selectedSlot) &&
    !isEmpty(selectedTime);

  const showTerms = canSkipStepTwo || currentStep === BOOKING_DRAWER_STEPS.TWO;

  const isBookingSlotOrDateChanged = useMemo(() => {
    if (!isEditBooking || !bookingEditData) return false;

    const dateChanged = selectedDate !== bookingEditData.startDate;
    const timeChanged = selectedTime !== bookingEditData.startTime;
    const locationChanged = locationId !== bookingEditData.location?.id;
    const resourceChanged =
      selectedSlot?.resourceId !== bookingEditData.resource?.id;
    const durationChanged =
      selectedSlot?.slot?.slot?.toString() !==
      bookingEditData.timeSlot?.toString();

    return (
      dateChanged ||
      timeChanged ||
      locationChanged ||
      resourceChanged ||
      durationChanged
    );
  }, [
    isEditBooking,
    bookingEditData,
    selectedDate,
    selectedTime,
    locationId,
    selectedSlot,
  ]);

  const hasDisabledMandatoryAmenity = useMemo(() => {
    return amenitiesList.some(amenity => {
      if (!amenity.isMandatory) return false;
      if (!isAmenityApplicable(amenity, selectedSlot)) return false;

      const isDisabled = checkIsAmenityDisabled(
        amenity,
        selectedSlot,
        selectedTime,
        selectedDate
      );

      const limitPerBooking =
        amenity.allowMultipleQuantity > 0 ? amenity.allowMultipleQuantity : 0;
      const availableCount = amenity.bookableCount?.availableCount ?? 0;
      const maxQty = Math.min(availableCount, limitPerBooking);

      if (isEditBooking) {
        const originalAmenity = bookingEditData?.amenities?.find(
          a => a.id === amenity.id
        );
        const isAlreadyPaid = originalAmenity?.isPaid === true;

        // If already paid, and slot parameters haven't changed, don't block
        if (isAlreadyPaid && !isBookingSlotOrDateChanged) {
          return false;
        }
      }

      if (!isDisabled && maxQty <= 0) return true;
      return false;
    });
  }, [
    amenitiesList,
    selectedSlot,
    selectedTime,
    selectedAmenitie,
    selectedDate,
    isBookingSlotOrDateChanged,
    bookingEditData,
    isEditBooking,
  ]);

  const hasUnavailableAddedAmenity = useMemo(() => {
    return selectedAmenitie.some(sa => {
      if (sa.isBooked) return false;
      const currentAmenity = availableAmenities.find(a => a.id === sa.id);
      if (!currentAmenity) return true;
      return checkIsAmenityDisabled(
        currentAmenity,
        selectedSlot,
        selectedTime,
        selectedDate
      );
    });
  }, [
    selectedAmenitie,
    availableAmenities,
    selectedSlot,
    selectedTime,
    selectedDate,
  ]);

  const isNextButtonDisabled = useMemo(() => {
    if (!canProceedToNextStep) {
      return true;
    }

    if (isEmpty(selectedSlot) || selectedSlot?.isBookable === false) {
      return true;
    }

    if (currentStep === BOOKING_DRAWER_STEPS.TWO || canSkipStepTwo) {
      if (!gdprAccepted || !legalTermsAccepted) {
        return true;
      }

      const hasPrice = grandTotal > 0;

      if (selectedPaymentMethod === "" && hasPrice) {
        return true;
      }

      if (hasPrice && !selectedPaymentMethod) {
        return true;
      }

      if (paymentLoader || priceLoader) {
        return true;
      }

      if (hasDisabledMandatoryAmenity) {
        return true;
      }

      if (hasUnavailableAddedAmenity || amenityAddedUnavailableError) {
        return true;
      }
    }

    return false;
  }, [
    canProceedToNextStep,
    currentStep,
    canSkipStepTwo,
    gdprAccepted,
    legalTermsAccepted,
    priceForResource,
    guestPriceAmount,
    selectedPaymentMethod,
    hasDisabledMandatoryAmenity,
    hasUnavailableAddedAmenity,
    amenityAddedUnavailableError,
    paymentLoader,
    selectedSlot,
    priceLoader,
    grandTotal,
  ]);

  const startEndTimeString = useMemo(() => {
    const { drawerSelectedTime } = drawerBookingData;
    if (!drawerSelectedTime) return "";

    const startMoment = moment(drawerSelectedTime, DateFormats.Time);
    const endTimeVal = moment(drawerSelectedTime, DateFormats.Time).add(
      drawerBookingData?.slot || 0,
      "minutes"
    );

    const startTime = startMoment.format(DateFormats.Time);

    if (!endTimeVal.isValid()) {
      return startTime;
    }

    const endTime = endTimeVal.format(DateFormats.Time);

    return `${startTime} - ${endTime}`;
  }, [drawerBookingData?.drawerSelectedTime, drawerBookingData?.slot]);

  const showTimeSection = useMemo(
    () => openPercent > DRAWER_HEIGHT.TWENTY_FIVE_PERCENT,
    [openPercent]
  );

  const buildAndSetTempEvent = useCallback(() => {
    const { drawerSelectedTime } = drawerBookingData;
    if (!drawerSelectedTime || !drawerBookingData?.resourceId) return;

    const selectedTimeSlot =
      selectedSlot?.slot?.slot || BOOKING_CONSTANTS.INTERVAL_15_MINUTES;

    const startDate = moment(
      selectedDate + "T" + drawerSelectedTime,
      DateFormats.YMDTime
    ).toDate();
    const endDate = moment(
      selectedDate + "T" + drawerSelectedTime,
      DateFormats.YMDTime
    )
      .add(selectedTimeSlot, "minutes")
      .toDate();

    const tempEvent = buildTempEvent({
      end: endDate,
      resourceId: drawerBookingData?.resourceId,
      start: startDate,
    });

    dispatch(
      drawerSlice.actions.setDrawerBookingData({
        ...drawerBookingData,
        slot: selectedTimeSlot,
      })
    );

    dispatch(bookingCalendarSlice.actions.setTempEvents([tempEvent]));
  }, [
    drawerBookingData?.drawerSelectedTime,
    selectedSlot?.slot?.slot,
    drawerBookingData?.resourceId,
    showTimeSection,
    dispatch,
    selectedDate,
  ]);

  useEffect(() => {
    buildAndSetTempEvent();
  }, [buildAndSetTempEvent]);
  useEffect(() => {
    setSelectedPaymentMethod("");
  }, [guestPriceAmount, priceForResource]);

  return (
    <div className="d-flex flex-column h-100" ref={drawerContainerRef}>
      <div className="flex-grow-1 overflow-auto">
        <div className="container">
          {!showTimeSection && (
            <div className="d-flex fs-6 fw-bold gap-1 flex-wrap">
              <div className="d-flex align-items-center gap-1 p-1 rounded">
                <FcCalendar size={20} className="text-light-blue fw-bold" />
                <span>{moment(selectedDate).format("DD.MM.YY")}</span>
              </div>
              <div className="d-flex align-items-center gap-1 p-1 rounded">
                <img
                  src={TimeIcon}
                  alt="Time Icon"
                  className="fg-drawer-icon"
                />
                <span>{startEndTimeString}</span>
              </div>

              <div className="d-flex align-items-center gap-1 p-1 rounded">
                <img
                  src={LocationPinIcon}
                  alt="Resource Location Icon"
                  className="fg-drawer-icon"
                />
                <span>{applyLocalization(currentResource?.title)}</span>
              </div>
            </div>
          )}
          {currentStep === BOOKING_DRAWER_STEPS.ONE && showTimeSection && (
            <BookingDrawerStepOne
              isBookingDisable={isBookingDisable}
              priceLoader={priceLoader}
              hasValidResource={hasValidResource}
              resource={currentResource}
              priceForResource={priceForResource}
              currency={currency}
            />
          )}

          {currentStep === BOOKING_DRAWER_STEPS.TWO && showTimeSection && (
            <BookingDrawerStepTwo
              payment={{
                selectedPaymentMethod: selectedPaymentMethod,
                setSelectedPaymentMethod: setSelectedPaymentMethod,
              }}
              bookingSlots={bookingSlots}
              guestError={guestError}
              allowBringGuest={allowBringGuest}
              selectedAmenitie={selectedAmenitie}
              setSelectedAmenities={setSelectedAmenities}
              amenitiesList={availableAmenities}
              setAmenitiesList={setAmenitiesList}
              onGrandTotalChange={setGrandTotal}
              grandTotal={grandTotal}
            />
          )}

          {showTerms && showTimeSection && (
            <div className="mt-4">
              <Terms
                gdpr={bookingUrls?.gdpr}
                legal_terms={bookingUrls?.legal_terms}
                gdprTerms={bookingUrls?.gdprTerms}
                termsService={bookingUrls?.termsService}
              />
            </div>
          )}
        </div>
      </div>
      {showTimeSection && (
        <div className="fg-drawer-footer z-3 border-top pt-2 mt-2 position-sticky bottom-0 py-2">
          <div className="container d-flex justify-content-between">
            <button
              data-testid={"drawer-back-button"}
              key={currentStep}
              className="btn fg-btn btn-default mb-0"
              onClick={handleBack}
            >
              {t("back")}
            </button>
            <button
              data-testid={"drawer-next-button"}
              key={
                canSkipStepTwo || currentStep === BOOKING_DRAWER_STEPS.TWO
                  ? "confirm"
                  : "next"
              }
              disabled={isNextButtonDisabled}
              className="btn fg-btn fg-btn-filled-darkblue mb-0"
              onClick={handleNext}
            >
              {currentStep !== BOOKING_DRAWER_STEPS.TWO && !canSkipStepTwo
                ? t("Next")
                : selectedPaymentMethod === PAYMENT_OPTIONS.INSTANT_PAYMENT
                  ? isSavedCardSelected || isNoAmountChange
                    ? t("ConfirmBooking")
                    : t("goToPayment")
                  : t("ConfirmBooking")}
            </button>
          </div>
        </div>
      )}
      <TokenisationConfirmModal
        isOpen={showPaymentConfirmModal}
        onCancel={() => {
          dispatch(bookingSlice.actions.setShowPaymentCancelWarning(true));
          setShowPaymentConfirmModal(false);
        }}
        onConfirm={onConfirmPayment}
      />
    </div>
  );
};

export default DrawerTimeSlot;
